import React from "react";
import { addDays, format, isToday, isTomorrow } from "date-fns";
import { pl } from "date-fns/locale";
import { CourtGridTimeSlot, CourtGridData } from "../../../../models/CourtGridData";
import { TimeSlot, TimeSlotData, SlotStatus } from "../../../shared/TimeSlot";
import { SaleStep } from "../../../../store/stepSaleConfig/stepSaleConfigReducer";
import "./style.scss";
import EAButton from "../../../shared/Buttons/EAButton";
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";

interface IProps {
  weeklyGridData: CourtGridData[];
  allHours: string[];
  findSlot: (date: string, startTime: string) => CourtGridTimeSlot | undefined;
  getSlotStatus: (timeSlot: CourtGridTimeSlot, slotDate: string) => SlotStatus;
  onSlotClick: (timeSlot: CourtGridTimeSlot) => void;
  saleStep?: SaleStep;
  onPreviousPeriod: () => void;
  onNextPeriod: () => void;
  startDate: Date;
}

const WeeklyGridTable: React.FC<IProps> = ({
  weeklyGridData,
  allHours,
  findSlot,
  getSlotStatus,
  onSlotClick,
  saleStep,
  onPreviousPeriod,
  onNextPeriod,
  startDate,
}) => {
  // Check if there are any available time slots
  const hasAvailableSlots = weeklyGridData.some((dayData) =>
    dayData.courts.some((court) => court.timeSlots && court.timeSlots.length > 0)
  );

  if (!hasAvailableSlots) {
    return (
      <div className="weekly-grid-table-empty">
        <p>Brak dostępnych terminów dla wybranego okresu.</p>
        <p>
          {format(startDate, "dd.MM.yyyy")} - {format(addDays(startDate, 6), "dd.MM.yyyy")}
        </p>
        <div className="weekly-grid-table-empty__buttons">
          <EAButton
            title="Poprzedni tydzień"
            onClick={() => onPreviousPeriod()}
            variant="outlined"
            startIcon={<MdOutlineKeyboardArrowLeft />}
          />
          <EAButton
            title="Następny tydzień"
            onClick={() => onNextPeriod()}
            variant="outlined"
            endIcon={<MdOutlineKeyboardArrowRight />}
          />
        </div>
      </div>
    );
  }

  const handleSlotClick = (data: TimeSlotData, contextDate: string) => {
    const slot = findSlot(contextDate, data.startTime);
    if (slot) {
      onSlotClick(slot);
    }
  };

  const getGridTemplateColumns = () => {
    const timeColumnWidth = window.innerWidth <= 480 ? 80 : window.innerWidth <= 768 ? 100 : 120;
    return `${timeColumnWidth}px repeat(${weeklyGridData.length}, 1fr)`;
  };

  const getDayHeader = (dateString: string) => {
    const date = new Date(dateString);
    const dayName = format(date, "EEEE", { locale: pl });
    const dateFormatted = format(date, "dd.MM");
    if (isToday(date)) {
      return `Dzisiaj ${dateFormatted}`;
    } else if (isTomorrow(date)) {
      return `Jutro ${dateFormatted}`;
    }
    return `${dayName} ${dateFormatted}`;
  };

  return (
    <div className="weekly-grid-table">
      <div className="weekly-grid-table__header" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
        <div className="weekly-grid-table__time-header">Godzina</div>
        {weeklyGridData.map((dayData) => (
          <div key={dayData.date} className="weekly-grid-table__day-header">
            <span className="weekly-grid-table__day-name">{getDayHeader(dayData.date)}</span>
          </div>
        ))}
      </div>

      <div className="weekly-grid-table__separator"></div>

      <div className="weekly-grid-table__body">
        {allHours.map((hour, hourIndex) => (
          <div key={hour} className="weekly-grid-table__row" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
            <div className="weekly-grid-table__time-cell">
              <p className="weekly-grid-table__time-text">{hour}</p>
            </div>
            {weeklyGridData.map((dayData) => {
              const slot = findSlot(dayData.date, hour);

              return (
                <div key={`${dayData.date}-${hour}`} className="weekly-grid-table__slot-cell">
                  {slot ? (
                    <TimeSlot
                      data={{
                        id: slot.exerciseId,
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        capacity: slot.capacity,
                        reserved: slot.reserved,
                        status: slot.status,
                        userAssigned: slot.userAssigned,
                      }}
                      contextName={dayData.date}
                      status={getSlotStatus(slot, dayData.date)}
                      onClick={handleSlotClick}
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                    />
                  ) : (
                    <TimeSlot
                      data={{
                        id: 0,
                        startTime: hour,
                        endTime: hour,
                        capacity: 0,
                        reserved: 0,
                      }}
                      contextName={dayData.date}
                      status="unavailable"
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeeklyGridTable;

